// @file Composable for Google Drive
/// <reference types="@types/google.picker" />

import { __ } from '@@/bits/intl'
import { NATIVE_HOST } from '@@/bits/url'
import { captureException } from 'bits/error_tracker'
import { User as UserApi } from 'dashboard/padlet_api'
import { SnackbarNotificationType } from 'enums'
import { useGlobalSnackbarStore } from 'pinia/global_snackbar'
import { useSettingsIntegrationPermissionsStore } from 'pinia/settings_integration_permissions'

export const useGoogleDrive = (): {
  requestGoogleDriveAuthorization: typeof requestGoogleDriveAuthorization
  removeGoogleDriveAuthorization: typeof removeGoogleDriveAuthorization
} => {
  const requestGoogleDriveAuthorization = async ({
    userId,
    tenantId,
  }: {
    userId: number
    tenantId: number
  }): Promise<{ email: string; token: string }> => {
    return await new Promise((resolve, reject) => {
      const googleOauthTokenResponseHandler = (result): void => {
        window.removeEventListener('message', googleOauthTokenResponseHandler)
        if (result.origin !== `https://${NATIVE_HOST}`) {
          reject(Error('Incorrect Origin'))
          return
        }
        return resolve({ email: result.data.detail.email, token: result.data.detail.accessToken })
      }
      window.addEventListener('message', googleOauthTokenResponseHandler)
      const oauthPath = `https://${NATIVE_HOST}/auth/google_oauth2?tenant_id=${tenantId}&scope_alias=drive&afterwards=dispatch_parent_drive&user_id=${userId}`
      const oauthWindow = window.open(oauthPath, 'google_oauth', 'height=600,width=450')
      if (oauthWindow != null) {
        oauthWindow.focus()
      }
    })
  }

  const removeGoogleDriveAuthorization = async (userId: number): Promise<void> => {
    // await an endpoint that will remove the google drive authorization
    try {
      await UserApi.removeGoogleDriveAuthorization(userId)
      useSettingsIntegrationPermissionsStore().clearGoogleDriveEmail()
    } catch (error) {
      void useGlobalSnackbarStore().setSnackbar({
        message: __('Something went wrong. Please try again later.'),
        notificationType: SnackbarNotificationType.error,
      })
      captureException(error)
    }
  }

  return {
    requestGoogleDriveAuthorization,
    removeGoogleDriveAuthorization,
  }
}
